import type { Brand } from "@webuild-ai/react-auth";

import ExcelJS from "exceljs";

import { Artefact, ArtefactType } from "@/types/artefacts";
import { logger } from "@/telemetry";

import { extractTablesFromHTML } from "../artefactFormatDetection";
import { sanitizeFilename } from "../fileUtils";
import { setMarkdownCell } from "../markdownToExcel";
import { parseTableContent, getColumnDataTypes } from "../tableUtils";

import { CapturedImage } from "./htmlToImage";

// Excel configuration constants
const EXCEL_SHEET_NAME_MAX_LENGTH = 31; // Excel sheet name character limit
const EXCEL_DEFAULT_COLUMN_WIDTH = 10; // Default minimum column width
const EXCEL_MAX_COLUMN_WIDTH = 50; // Maximum column width for auto-sizing
const EXCEL_COLUMN_WIDTH_PADDING = 2; // Extra padding for column width
const EXCEL_TITLE_MERGE_COLUMNS = 10; // Number of columns to merge for titles
const EXCEL_ARTEFACT_SPACING = 2; // Spacing between artefacts
const EXCEL_CODE_MIN_ROWS = 5; // Minimum rows for code display
const EXCEL_OVERVIEW_COL1_WIDTH = 50; // Overview sheet column 1 width
const EXCEL_OVERVIEW_COL2_WIDTH = 15; // Overview sheet column 2 width
const EXCEL_OVERVIEW_COL3_WIDTH = 25; // Overview sheet column 3 width
const EXCEL_HEADER_FONT_SIZE = 12; // Header font size
const EXCEL_CODE_FONT_SIZE = 10; // Code font size
const EXCEL_HTML_TABLE_FONT_SIZE = 11; // HTML table font size
const EXCEL_FALLBACK_FONT_SIZE = 11; // Fallback message font size

// Image embedding constants
const IMAGE_SCALE_DIVISOR = 2; // Scale down captured images (captured at 2x)
const IMAGE_MAX_WIDTH = 1200; // Maximum image width in pixels
const IMAGE_MAX_HEIGHT = 800; // Maximum image height in pixels
const IMAGE_ROW_HEIGHT_APPROX = 20; // Approximate Excel row height in pixels

/**
 * Extract a display text length from any ExcelJS cell value, including
 * rich-text and hyperlink objects produced by `setMarkdownCell`. Falling back
 * to `value.toString()` would yield "[object Object]" for those shapes.
 */
const getCellTextLength = (value: ExcelJS.CellValue): number => {
  if (value === null || value === undefined) return 0;

  if (typeof value === "object") {
    if ("richText" in value && Array.isArray(value.richText)) {
      return value.richText.reduce((sum, segment) => sum + (segment.text?.length ?? 0), 0);
    }
    if ("text" in value && typeof value.text === "string") {
      return value.text.length;
    }
    if ("formula" in value && "result" in value && value.result !== undefined) {
      return String(value.result).length;
    }
  }

  return String(value).length;
};

export interface ExcelExportLabels {
  exportedContent?: string; // Worksheet name for single-sheet mode (default: "Exported Content")
  overview?: string; // Overview sheet name for multi-sheet mode (default: "Overview")
  artefactColumn?: string; // Overview column header (default: "Artefact")
  typeColumn?: string; // Overview column header (default: "Type")
  createdColumn?: string; // Overview column header (default: "Created")
}

export interface ExcelExportOptions {
  useSeparateWorksheets?: boolean;
  fontFamily?: string; // Body text font (from brand or user preference)
  codeFontFamily?: string; // Code blocks font (default: "Consolas")
  labels?: ExcelExportLabels; // Translated UI labels (defaults to English)
}

/**
 * Export multiple artefacts to Excel format with ExcelJS (supports images)
 * @param artefacts Array of artefacts to export
 * @param options Export options (single vs multiple worksheets)
 * @param title Custom title for the export
 * @param brand Brand configuration for styling
 * @param theme Current theme (light/dark)
 * @param images Captured images for HTML artefacts (optional)
 */
export const batchExportToExcel = async (
  artefacts: Artefact[],
  options: ExcelExportOptions,
  title: string,
  brand: Brand,
  theme: "light" | "dark",
  images?: Map<string, CapturedImage>,
): Promise<void> => {
  try {
    const workbook = new ExcelJS.Workbook();

    workbook.creator = "WeBuild Invest AI";
    workbook.created = new Date();

    // Extract font options with defaults
    const fontFamily = options.fontFamily; // undefined = Excel default
    const codeFontFamily = options.codeFontFamily || "Consolas";

    if (options.useSeparateWorksheets) {
      await createMultipleWorksheets(
        workbook,
        artefacts,
        brand,
        theme,
        images,
        fontFamily,
        codeFontFamily,
        options.labels,
      );
    } else {
      await createSingleWorksheet(
        workbook,
        artefacts,
        brand,
        theme,
        images,
        fontFamily,
        codeFontFamily,
        options.labels,
      );
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const filename = `${sanitizeFilename(title)}_${timestamp}.xlsx`;

    // Write file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    logger.error("Failed to export to Excel:", error);
    throw new Error("Excel export failed");
  }
};

/**
 * Create a single worksheet with all artefacts stacked vertically
 */
const createSingleWorksheet = async (
  workbook: ExcelJS.Workbook,
  artefacts: Artefact[],
  brand: Brand,
  theme: "light" | "dark",
  images?: Map<string, CapturedImage>,
  fontFamily?: string,
  codeFontFamily?: string,
  labels?: ExcelExportLabels,
): Promise<void> => {
  const worksheet = workbook.addWorksheet(labels?.exportedContent || "Exported Content");
  let currentRow = 1;

  for (const artefact of artefacts) {
    // Per-artefact calculated titles are intentionally omitted from exports.
    // Only the document-level title (from user input) appears as a heading.

    // Add content based on artefact type
    switch (artefact.type) {
      case ArtefactType.CODE:
        currentRow = addCodeArtefact(worksheet, currentRow, artefact, codeFontFamily);
        break;
      case ArtefactType.DOCUMENT:
        currentRow = addDocumentArtefact(worksheet, currentRow, artefact, fontFamily);
        break;
      case ArtefactType.TABLE:
        currentRow = await addTableArtefact(
          worksheet,
          currentRow,
          artefact,
          brand,
          theme,
          fontFamily,
        );
        break;
      case ArtefactType.HTML:
        currentRow = await addHtmlArtefact(
          workbook,
          worksheet,
          currentRow,
          artefact,
          brand,
          theme,
          images,
          fontFamily,
        );
        break;
    }

    currentRow += EXCEL_ARTEFACT_SPACING; // Space between artefacts
  }

  // Auto-fit columns
  worksheet.columns.forEach((column) => {
    const fullColumn = column as ExcelJS.Column;

    if (fullColumn && fullColumn.eachCell) {
      let maxLength = EXCEL_DEFAULT_COLUMN_WIDTH;

      fullColumn.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell) => {
        const length = getCellTextLength(cell.value);

        if (length > maxLength) {
          maxLength = Math.min(length, EXCEL_MAX_COLUMN_WIDTH);
        }
      });
      fullColumn.width = maxLength + EXCEL_COLUMN_WIDTH_PADDING;
    }
  });
};

/**
 * Create multiple worksheets, one per artefact
 */
const createMultipleWorksheets = async (
  workbook: ExcelJS.Workbook,
  artefacts: Artefact[],
  brand: Brand,
  theme: "light" | "dark",
  images?: Map<string, CapturedImage>,
  fontFamily?: string,
  codeFontFamily?: string,
  labels?: ExcelExportLabels,
): Promise<void> => {
  // Create overview sheet
  const overview = workbook.addWorksheet(labels?.overview || "Overview");

  createOverviewSheet(overview, artefacts, brand, theme, fontFamily, labels);

  // Create individual sheets for each artefact
  for (const artefact of artefacts) {
    // Sanitize and truncate sheet name (Excel limit: 31 chars)
    let sheetName = sanitizeFilename(artefact.title).slice(0, EXCEL_SHEET_NAME_MAX_LENGTH);

    // Ensure unique sheet names
    let counter = 1;
    let finalSheetName = sheetName;

    while (workbook.getWorksheet(finalSheetName)) {
      const suffix = ` (${counter})`;

      finalSheetName = sheetName.slice(0, EXCEL_SHEET_NAME_MAX_LENGTH - suffix.length) + suffix;
      counter++;
    }

    const worksheet = workbook.addWorksheet(finalSheetName);
    let currentRow = 1;

    // Per-artefact calculated titles are intentionally omitted from exports.
    // Sheet tab names still use artefact.title for structural navigation.

    // Add content
    switch (artefact.type) {
      case ArtefactType.CODE:
        currentRow = addCodeArtefact(worksheet, currentRow, artefact, codeFontFamily);
        break;
      case ArtefactType.DOCUMENT:
        currentRow = addDocumentArtefact(worksheet, currentRow, artefact, fontFamily);
        break;
      case ArtefactType.TABLE:
        currentRow = await addTableArtefact(
          worksheet,
          currentRow,
          artefact,
          brand,
          theme,
          fontFamily,
        );
        break;
      case ArtefactType.HTML:
        currentRow = await addHtmlArtefact(
          workbook,
          worksheet,
          currentRow,
          artefact,
          brand,
          theme,
          images,
          fontFamily,
        );
        break;
    }

    // Auto-fit columns
    worksheet.columns.forEach((column) => {
      const fullColumn = column as ExcelJS.Column;

      if (fullColumn && fullColumn.eachCell) {
        let maxLength = EXCEL_DEFAULT_COLUMN_WIDTH;

        fullColumn.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell) => {
          const length = getCellTextLength(cell.value);

          if (length > maxLength) {
            maxLength = Math.min(length, EXCEL_MAX_COLUMN_WIDTH);
          }
        });
        fullColumn.width = maxLength + EXCEL_COLUMN_WIDTH_PADDING;
      }
    });
  }
};

/**
 * Create overview sheet with table of contents
 */
const createOverviewSheet = (
  worksheet: ExcelJS.Worksheet,
  artefacts: Artefact[],
  brand: Brand,
  theme: "light" | "dark",
  fontFamily?: string,
  labels?: ExcelExportLabels,
): void => {
  // Add headers
  const headerRow = worksheet.getRow(1);

  headerRow.values = [
    labels?.artefactColumn || "Artefact",
    labels?.typeColumn || "Type",
    labels?.createdColumn || "Created",
  ];
  headerRow.font = {
    bold: true,
    color: { argb: "FFFFFFFF" },
    size: EXCEL_HEADER_FONT_SIZE,
    name: fontFamily,
  };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: brand.colors[theme].primary.replace("#", "FF") },
  };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };

  // Add data rows
  artefacts.forEach((artefact, index) => {
    const row = worksheet.getRow(index + 2);

    row.values = [artefact.title, artefact.type, new Date().toLocaleString()];
    if (fontFamily) {
      row.font = { name: fontFamily };
    }
  });

  // Set column widths
  worksheet.getColumn(1).width = EXCEL_OVERVIEW_COL1_WIDTH;
  worksheet.getColumn(2).width = EXCEL_OVERVIEW_COL2_WIDTH;
  worksheet.getColumn(3).width = EXCEL_OVERVIEW_COL3_WIDTH;

  // Add auto-filter
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: artefacts.length + 1, column: 3 },
  };
};

/**
 * Add CODE artefact to worksheet
 */
const addCodeArtefact = (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  artefact: Artefact,
  codeFontFamily?: string,
): number => {
  const cell = worksheet.getCell(startRow, 1);

  cell.value = artefact.content;
  cell.font = { name: codeFontFamily || "Consolas", size: EXCEL_CODE_FONT_SIZE };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF5F5F5" },
  };
  cell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
  cell.border = {
    top: { style: "thin", color: { argb: "FFCCCCCC" } },
    bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
    left: { style: "thin", color: { argb: "FFCCCCCC" } },
    right: { style: "thin", color: { argb: "FFCCCCCC" } },
  };

  // Calculate number of lines for row span
  const lines = artefact.content.split("\n").length;
  const endRow = startRow + Math.max(lines, EXCEL_CODE_MIN_ROWS) - 1;

  // Merge across columns
  worksheet.mergeCells(startRow, 1, endRow, EXCEL_TITLE_MERGE_COLUMNS);

  return endRow + 1;
};

/**
 * Add DOCUMENT artefact to worksheet
 */
const addDocumentArtefact = (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  artefact: Artefact,
  fontFamily?: string,
): number => {
  // Strip markdown formatting for plain text
  const plainText = artefact.content
    .replace(/\*\*(.+?)\*\*/g, "$1") // Bold
    .replace(/\*(.+?)\*/g, "$1") // Italic
    .replace(/`(.+?)`/g, "$1") // Inline code
    .replace(/^#+\s+/gm, "") // Headers
    .replace(/^\s*[-*+]\s+/gm, "• ") // Bullet lists
    .replace(/^\s*\d+\.\s+/gm, ""); // Numbered lists

  // Split into paragraphs
  const paragraphs = plainText
    .split("\n\n")
    .filter((p) => p.trim())
    .map((p) => p.trim());

  let currentRow = startRow;

  paragraphs.forEach((paragraph) => {
    const cell = worksheet.getCell(currentRow, 1);

    cell.value = paragraph;
    cell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
    if (fontFamily) {
      cell.font = { name: fontFamily };
    }

    // Merge across columns for full width
    worksheet.mergeCells(currentRow, 1, currentRow, EXCEL_TITLE_MERGE_COLUMNS);

    currentRow += 1;
  });

  return currentRow;
};

/**
 * Add TABLE artefact to worksheet
 */
const addTableArtefact = async (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  artefact: Artefact,
  brand: Brand,
  theme: "light" | "dark",
  fontFamily?: string,
): Promise<number> => {
  try {
    const table = parseTableContent(artefact.content);

    if (!table || !table.headers || !table.rows) {
      throw new Error("Invalid table data");
    }

    // Add headers with brand styling
    const headerRow = worksheet.getRow(startRow);
    const headerBaseFont: Partial<ExcelJS.Font> = {
      bold: true,
      color: { argb: "FFFFFFFF" },
      size: EXCEL_HEADER_FONT_SIZE,
      name: fontFamily,
    };

    table.headers.forEach((header, colIndex) => {
      setMarkdownCell(headerRow.getCell(colIndex + 1), header, headerBaseFont);
    });
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: brand.colors[theme].primary.replace("#", "FF") },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    // Get column data types for proper formatting
    const rowObjects = table.rows.map((row) => {
      const obj: Record<string, any> = {};

      table.headers.forEach((header, idx) => {
        obj[header] = row[idx];
      });

      return obj;
    });
    const columnTypes = getColumnDataTypes(table.headers, rowObjects);

    // Add data rows
    const baseDataFont: Partial<ExcelJS.Font> | undefined = fontFamily
      ? { name: fontFamily }
      : undefined;

    table.rows.forEach((row, rowIndex) => {
      const excelRow = worksheet.getRow(startRow + rowIndex + 1);

      table.headers.forEach((header, colIndex) => {
        const cellValue = row[header];
        const cell = excelRow.getCell(colIndex + 1);
        const columnType = columnTypes[header];

        // Apply cell type based on detected column type
        if (columnType === "number" && !isNaN(parseFloat(cellValue))) {
          cell.value = parseFloat(cellValue);
          cell.numFmt = "#,##0.00";
          if (baseDataFont) cell.font = baseDataFont;
        } else if (columnType === "boolean") {
          cell.value = cellValue === "true" || cellValue === true;
          if (baseDataFont) cell.font = baseDataFont;
        } else {
          setMarkdownCell(cell, cellValue, baseDataFont);
        }
      });
    });

    // Auto-size columns based on content
    table.headers.forEach((header, colIndex) => {
      const column = worksheet.getColumn(colIndex + 1);
      const maxLength = Math.max(
        header.length,
        ...table.rows.map((row) => (row[header] || "").toString().length),
      );

      column.width = Math.min(maxLength + EXCEL_COLUMN_WIDTH_PADDING, EXCEL_MAX_COLUMN_WIDTH);
    });

    // Add auto-filter
    worksheet.autoFilter = {
      from: { row: startRow, column: 1 },
      to: { row: startRow + table.rows.length, column: table.headers.length },
    };

    return startRow + table.rows.length + 1;
  } catch (error) {
    logger.error("Failed to parse table:", error);

    // Fallback: add error message
    const cell = worksheet.getCell(startRow, 1);

    cell.value = "Error parsing table data";
    cell.font = { italic: true, color: { argb: "FFFF0000" } };

    return startRow + 1;
  }
};

/**
 * Add HTML artefact to worksheet (with image support!)
 */
const addHtmlArtefact = async (
  workbook: ExcelJS.Workbook,
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  artefact: Artefact,
  _brand: Brand,
  _theme: "light" | "dark",
  images?: Map<string, CapturedImage>,
  fontFamily?: string,
): Promise<number> => {
  // Try to extract tables from HTML first
  const tables = extractTablesFromHTML(artefact.content);

  if (tables.length > 0) {
    // Render first table found
    const table = tables[0];
    let currentRow = startRow;

    // Add table headers
    const headerRow = worksheet.getRow(currentRow);
    const headerBaseFont: Partial<ExcelJS.Font> = {
      bold: true,
      color: { argb: "FFFFFFFF" },
      size: EXCEL_HTML_TABLE_FONT_SIZE,
      name: fontFamily,
    };

    table.headers.forEach((header, colIndex) => {
      setMarkdownCell(headerRow.getCell(colIndex + 1), header, headerBaseFont);
    });
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };

    currentRow++;

    // Add table rows
    const baseDataFont: Partial<ExcelJS.Font> | undefined = fontFamily
      ? { name: fontFamily }
      : undefined;

    table.rows.forEach((row) => {
      const excelRow = worksheet.getRow(currentRow);

      row.forEach((cellValue, colIndex) => {
        setMarkdownCell(excelRow.getCell(colIndex + 1), cellValue, baseDataFont);
      });
      currentRow++;
    });

    return currentRow;
  }

  // No tables found - try to add image if available
  const image = images?.get(artefact.id);

  if (image) {
    try {
      // Convert base64 to buffer
      const base64Data = image.base64.replace(/^data:image\/\w+;base64,/, "");
      const imageId = workbook.addImage({
        base64: base64Data,
        extension: "png",
      });

      // Calculate image dimensions for Excel (Excel uses EMUs - English Metric Units)
      // Images captured at 2x scale for quality - use at 50% of captured size
      let width = image.width / IMAGE_SCALE_DIVISOR; // Scale down from 2x capture to display size
      let height = image.height / IMAGE_SCALE_DIVISOR;

      // Scale down if too large while maintaining aspect ratio
      if (width > IMAGE_MAX_WIDTH) {
        const ratio = IMAGE_MAX_WIDTH / width;

        width = IMAGE_MAX_WIDTH;
        height = height * ratio;
      }

      if (height > IMAGE_MAX_HEIGHT) {
        const ratio = IMAGE_MAX_HEIGHT / height;

        height = IMAGE_MAX_HEIGHT;
        width = width * ratio;
      }

      // Add image to worksheet
      worksheet.addImage(imageId, {
        tl: { col: 0, row: startRow - 1 }, // Top-left corner (0-indexed)
        ext: { width: width, height: height },
        editAs: "oneCell",
      });

      // Calculate how many rows the image spans
      const rowsSpanned = Math.ceil(height / IMAGE_ROW_HEIGHT_APPROX);

      return startRow + rowsSpanned + 1;
    } catch (error) {
      logger.error("Failed to add image to Excel:", error);
      // Fall through to placeholder
    }
  }

  // Fallback: add placeholder text
  const cell = worksheet.getCell(startRow, 1);

  cell.value = `📊 Chart: ${artefact.title}\n\nChart visualization has been captured but could not be embedded.\nPlease export to PowerPoint, Word, or PDF for full chart visualization.`;
  cell.font = { italic: true, color: { argb: "FFFF6B00" }, size: EXCEL_FALLBACK_FONT_SIZE };
  cell.alignment = { wrapText: true, vertical: "top" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFF4E6" },
  };

  worksheet.mergeCells(startRow, 1, startRow + 3, 3);

  return startRow + 4;
};
